import React, { FunctionComponent, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { AsyncSelect } from 'core/select/Select';
import { FacebookGeoLocation } from 'social/publisher/Publisher.types';
import { fetchFacebookGeoLocations } from 'social/publisher/PublisherFetchers';

interface Props {
    geoLocations: FacebookGeoLocation | undefined;
    tenantId: number | undefined;
    save: any;
}
type Option = {
    value: string;
    label: string;
    type: string;
    name: string;
    region?: string;
    country?: string;
};
const OVERLAY_AREA = 'social-publisher-post-target-location';
const buildGeoLocationOptions = (facebookGeoLocations: FacebookGeoLocation): Option[] => {
    let options: Option[] = [];

    if (facebookGeoLocations.countries && facebookGeoLocations.countries.length > 0) {
        const countryOptions = facebookGeoLocations.countries.map((el) => {
            const { key, name } = el;

            return {
                value: key,
                label: name,
                type: 'country',
                name,
            };
        });

        options = [...options, ...countryOptions];
    }

    if (facebookGeoLocations.regions && facebookGeoLocations.regions.length > 0) {
        const regionOptions = facebookGeoLocations.regions.map((el) => {
            const { key, name, country } = el;
            const labelData = [name];

            if (country) {
                labelData.push(country);
            }

            return {
                value: key,
                label: labelData.join(', '),
                type: 'region',
                name,
                country,
            };
        });

        options = [...options, ...regionOptions];
    }

    if (facebookGeoLocations.cities && facebookGeoLocations.cities.length > 0) {
        const cityOptions = facebookGeoLocations.cities.map((el) => {
            const { key, name, region, country } = el;
            const labelData = [name];

            if (region) {
                labelData.push(region);
            }
            if (country) {
                labelData.push(country);
            }

            return {
                value: key,
                label: labelData.join(', '),
                type: 'city',
                name,
                region,
                country,
            };
        });

        options = [...options, ...cityOptions];
    }

    return options;
};
const extractGeoLocationFromOptions = (options: Option[]): FacebookGeoLocation => {
    const locations: FacebookGeoLocation = {};

    if (options.length > 0) {
        options.forEach((el) => {
            if (el.type === 'country') {
                const location = { key: el.value, name: el.name };

                if (locations.countries) {
                    locations.countries.push(location);
                } else {
                    locations.countries = [location];
                }
            } else if (el.type === 'region') {
                const location = { key: el.value, name: el.name, country: el.country };

                if (locations.regions) {
                    locations.regions.push(location);
                } else {
                    locations.regions = [location];
                }
            } else if (el.type === 'city') {
                const location = { key: el.value, name: el.name, region: el.region, country: el.country };

                if (locations.cities) {
                    locations.cities.push(location);
                } else {
                    locations.cities = [location];
                }
            }
        });
    }

    return locations;
};

export const LocationField: FunctionComponent<Props> = ({ geoLocations, tenantId, save }) => {
    const { t } = useTranslation('social/publisher');
    let selectedOptions: Option[] = [];

    if (geoLocations) {
        selectedOptions = buildGeoLocationOptions(geoLocations);
    }
    const handleLocationsChange = (selectedOptions: Option[]): void => {
        let newLocations: FacebookGeoLocation = {};

        if (selectedOptions) {
            newLocations = extractGeoLocationFromOptions(selectedOptions);
        }

        save(newLocations);
    };

    const loadGeolocationOptions = useCallback(
        async (input: string): Promise<Option[]> => {
            let facebookGeoLocations: FacebookGeoLocation = {};
            const query = input.trim() ? input.trim() : 'cal';

            if (query && query.length > 1) {
                facebookGeoLocations = await trackPromise(fetchFacebookGeoLocations(tenantId, query), OVERLAY_AREA);
            }

            return buildGeoLocationOptions(facebookGeoLocations);
        },
        [tenantId],
    );

    return (
        <AsyncSelect
            name="locations"
            classNamePrefix="audience-targeting-location"
            className="location-select"
            loadOptions={loadGeolocationOptions}
            value={selectedOptions}
            onChange={handleLocationsChange}
            placeholder={t('SEARCH_LOCATIONS')}
            isMulti
            cacheOptions
            defaultOptions
        />
    );
};
